import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import axios from "axios";
import { API_BASE_URL } from "../config";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [trikots, setTrikots] = useState([]);

  // Benutzer laden
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else {
        throw new Error("Ungültiges Datenformat vom Server");
      }
    } catch (error) {
      setError("Fehler beim Laden der Benutzer: " + (error.response?.data?.message || error.message));
      setUsers([]);
    }
  };

  // Trikots laden
  const fetchTrikots = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/trikots`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.trikots) {
        setTrikots(response.data.trikots);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Trikots:", error);
      // Fehler wird stillschweigend ignoriert, da Trikots optional sind
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTrikots();
  }, []);

  // Berechne Statistiken für einen Benutzer
  const getUserStats = (username) => {
    const userTrikots = trikots.filter(trikot => trikot.highestBidder === username);
    return {
      trikotCount: userTrikots.length,
      totalBidSum: userTrikots.reduce((sum, trikot) => sum + trikot.currentBid, 0)
    };
  };

  // Status ändern (aktiv/inaktiv)
  const handleStatusChange = async (userId, newStatus) => {
    try {
      console.log("Attempting to change status:", { userId, newStatus });

      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${API_BASE_URL}/api/users/${userId}/status`,
        { isActive: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Status change response:", response.data);

      await fetchUsers();
      setSuccess("Benutzerstatus erfolgreich aktualisiert");
    } catch (error) {
      console.error("Status change error:", error);
      setError("Fehler beim Aktualisieren des Status: " + (error.response?.data?.message || error.message));
    }
  };

  // Admin-Rolle ändern
  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_BASE_URL}/api/users/${userId}/role`,
        { isAdmin: newRole },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await fetchUsers();
      setSuccess("Benutzerrolle erfolgreich aktualisiert");
    } catch (error) {
      setError("Fehler beim Aktualisieren der Rolle: " + (error.response?.data?.message || error.message));
    }
  };

  // Benutzer löschen
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/users/${selectedUser._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchUsers();
      setSuccess("Benutzer erfolgreich gelöscht");
      setDeleteDialogOpen(false);
    } catch (error) {
      setError("Fehler beim Löschen des Benutzers: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
        Benutzerverwaltung
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vorname</TableCell>
              <TableCell>Nachname</TableCell>
              <TableCell>Benutzername</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="right">Höchstgebote</TableCell>
              <TableCell align="right">Gesamtsumme</TableCell>
              <TableCell align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const stats = getUserStats(user.username);
              return (
                <TableRow key={user._id}>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={user.isActive}
                      onChange={(e) =>
                        handleStatusChange(user._id, e.target.checked)
                      }
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleRoleChange(user._id, !user.isAdmin)}
                      color={user.isAdmin ? "primary" : "default"}
                    >
                      <AdminPanelSettingsIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">{stats.trikotCount}</TableCell>
                  <TableCell align="right">{stats.totalBidSum.toFixed(2)}€</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => {
                        setSelectedUser(user);
                        setDeleteDialogOpen(true);
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Benutzer löschen</DialogTitle>
        <DialogContent>
          Möchten Sie wirklich den Benutzer "{selectedUser?.username}" löschen?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDelete} color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
